/**
 * The public, indexable pages: one list behind sitemap.xml, llms.txt, the
 * `.md` twins and llms-full.txt (lib/seo/llms.ts), so a new page shows up in
 * all four or none. Excluded on purpose: `/thanks` and `/demo-requested`
 * (per-visitor confirmations), `/consent` (a utility page, noindex),
 * `/account/*`, `/api/*` and `/dev/*`.
 *
 * `title` and `summary` are the link text and note in llms.txt, written for
 * an agent deciding which page answers its question.
 */
import { pricing, usd } from '$lib/content/pricing';

export type PageSection = 'Product' | 'Guides' | 'Case studies' | 'Buying' | 'Legal';

export interface PublicPage {
	path: string;
	title: string;
	summary: string;
	section: PageSection;
}

export const publicPages: readonly PublicPage[] = [
	{
		path: '/',
		title: 'PPF Engine home',
		section: 'Product',
		summary:
			'What the software does, the scan, flatten and cut workflow on a real panel, proof jobs, presale pricing and the short FAQ.'
	},
	{
		path: '/ppf-pattern-software',
		title: 'PPF pattern software',
		section: 'Product',
		summary:
			'The product page: one Mercedes-AMG GT front bumper from scan to cut file, the export formats, the presale terms and the workflow demo request.'
	},
	{
		path: '/guides/how-to-make-custom-ppf-patterns',
		title: 'How to make custom PPF patterns',
		section: 'Guides',
		summary:
			'The five ways installers make their own PPF patterns and templates (editing a library pattern, ordering one, tape lift, photo tracing, 3D scan and flatten) compared, the eight steps from coverage to test fit, and the mistakes that waste film.'
	},
	{
		path: '/technology/3d-scan-to-ppf-pattern',
		title: 'How a 3D vehicle scan becomes a PPF cutting pattern',
		section: 'Guides',
		summary:
			'The seven-step workflow: capture, clean, coverage and seams, flatten, check against the film, edit and export, test fit.'
	},
	{
		path: '/technology/scanner-plotter-compatibility',
		title: 'Scanner and plotter compatibility',
		section: 'Guides',
		summary:
			'Which scanners and plotters are tested in our studio, which are only file-compatible, which are not supported, and the computer requirements for macOS and Windows.'
	},
	{
		path: '/compare/ppf-engine-vs-exactflat',
		title: 'PPF Engine vs ExactFlat',
		section: 'Guides',
		// Mirrors the page's "Which one fits your shop?" lists and price box
		// (ExactFlat's figure is the reseller listing dated on that page).
		summary: `Where each fits. PPF Engine is built only for PPF installers: one standalone app with no CAD program to learn or license, seams, relief cuts, edge wraps and sensor cutouts handled as PPF decisions, and stretch checked against the film before cutting, at ${usd(pricing.founderYear)} for the first year against USD 6,995 for an ExactFlat seat (US reseller listing, 2026-09-19) plus a Rhino licence. ExactFlat fits shops that need software this month, already work in Rhino, or pattern more than film.`
	},
	{
		path: '/case-studies/ram-1500-fender-flare',
		title: 'Case study: RAM 1500 bolt-on fender flare',
		section: 'Case studies',
		summary:
			'An aftermarket flare no pattern library had. One scan in, one plotter-ready file out, with all nine bolt recesses traced off the mesh.'
	},
	{
		path: '/case-studies/bmw-x3-30e-fender-charging-cap',
		title: 'Case study: BMW X3 30e fender and charging cap',
		section: 'Case studies',
		summary:
			'Custom templates for the plug-in hybrid variant’s fender and charging cap, which no pattern library covered.'
	},
	{
		path: '/pricing',
		title: 'Pricing',
		section: 'Buying',
		summary: `The presale license: ${usd(pricing.founderYear)} for the first year, ${pricing.discountPct}% off the ${usd(pricing.annual)}/yr launch price, refundable until access begins.`
	},
	{
		path: '/faq',
		title: 'FAQ',
		section: 'Buying',
		summary:
			'Every question we get: what you buy, renewal, seats, delivery, availability, tested hardware, test fitting, computer requirements and who is behind it.'
	},
	{
		path: '/legal/preorder',
		title: 'Pre-order terms',
		section: 'Legal',
		summary:
			'The presale contract: what a pre-order is, refunds and withdrawal, what happens if delivery slips, who we sell to and what you get.'
	},
	{
		path: '/legal/privacy',
		title: 'Privacy policy',
		section: 'Legal',
		summary: 'What personal data the site and checkout collect, why, and for how long.'
	},
	{
		path: '/legal/terms',
		title: 'Terms',
		section: 'Legal',
		summary: 'Terms for using this website: intellectual property, liability and contact.'
	},
	{
		path: '/legal/imprint',
		title: 'Imprint',
		section: 'Legal',
		summary: 'Company details: Andify OÜ, registry code, VAT number and address in Tartu, Estonia.'
	}
];

export function publicPage(path: string): PublicPage | undefined {
	return publicPages.find((p) => p.path === path);
}

/**
 * Where a page's Markdown twin lives: the page URL with `.md` appended, and
 * `/index.html.md` for the root, per the llms.txt proposal (llmstxt.org).
 */
export function markdownPath(path: string): string {
	return path === '/' ? '/index.html.md' : `${path}.md`;
}
